import { Link, useLoaderData } from '@remix-run/react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import PlentyBody from '#app/components/typography/plenty-body'
import ClerkProductCard from '#app/features/plp-clerk/clerk-components/clerk-product-card/clerk-product-card'
import { type loader as PDPLoader } from '#app/routes/_public+/product+/$styleNumber'
import { useClerkVisitorStore } from '#app/store/clerk-visitor'
import { logClickEvent } from '#app/utils/tracking/clerk-tracking'
import { type ClerkProduct, type PlentyProduct } from '#types/product'

const ClerkProductsVerticalTwoByTwoSimilarProducts = () => {
	let { t } = useTranslation('product_details_page')
	const clerkLoaderData = useLoaderData<typeof PDPLoader>()
	const { clerkVisitor } = useClerkVisitorStore()

	if (
		!clerkLoaderData ||
		!clerkLoaderData.productData ||
		!clerkLoaderData.similarClerkProductData
	) {
		return null
	}

	const similarClerkProductData = clerkLoaderData.similarClerkProductData

	const hasPopularData =
		similarClerkProductData && similarClerkProductData.product_data?.length > 0

	const similarLinkParams = []
	const brandLinkParams = []
	const activeStyle =
		clerkLoaderData.productData.styleAvailableVariants.find(
			(it: { active: any }) => it.active,
		) || clerkLoaderData.productData.styleAvailableVariants[0]

	const bodyType = activeStyle?.variants[0].attributes?.find(
		(item: { name: string }) => item.name === 'body_type',
	)?.value
	const brand = clerkLoaderData.productData.brand?.value.en

	if (brand) {
		const brandParamsString = `brand=${encodeURIComponent(brand)}&`
		brandLinkParams.push(brandParamsString)
	}

	if (bodyType?.en?.length) {
		const bodyParamsString = `bodytype=${encodeURIComponent(bodyType.en)}&`
		if (bodyParamsString) {
			similarLinkParams.push(bodyParamsString)
			brandLinkParams.push(bodyParamsString)
		}
	}
	if (clerkLoaderData?.storeParams) {
		const storeParamsString = clerkLoaderData.storeParams
			.map((store: string | number | boolean) => `${encodeURIComponent(store)}`)
			.join(',')
		if (storeParamsString) {
			similarLinkParams.push(`store=${storeParamsString}`)
			brandLinkParams.push(`store=${storeParamsString}`)
		}
	}

	const isClient = typeof window !== 'undefined'

	const handleClick = (
		product: ClerkProduct | PlentyProduct,
		index: number,
	) => {
		if (clerkVisitor && 'style_id' in product) {
			logClickEvent(product.id, index, clerkVisitor)
		}
	}

	if (!hasPopularData) {
		return null
	}

	return (
		<div className="flex flex-col gap-y-8">
			<div className="px-4 md:pl-[var(--plenty-padding)] md:pr-0">
				<div>
					<div className="mb-4">
						<PlentyBody className="font-semibold" size="lg">
							{t('popular_products')}
						</PlentyBody>
					</div>
					<div className="grid grid-cols-2 gap-2">
						{similarClerkProductData.product_data.map(
							(product: ClerkProduct, index: number) => (
								<Link
									key={product.id}
									to={`/product/${product.style_id}${
										clerkLoaderData?.storeParams?.length
											? `&store=${clerkLoaderData.storeParams.join(',')}`
											: ''
									}`}
									state={{
										amplitudePageSource: isClient ? window.location.href : '',
									}}
									onClick={() => handleClick(product, index)}
								>
									<ClerkProductCard
										product={product}
										lazyLoad={true}
										width="50px"
										className="overflow-hidden [&_img]:mb-[-22px]"
									/>
								</Link>
							),
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

export default ClerkProductsVerticalTwoByTwoSimilarProducts
